<template>
  <div>
    <div class="row page-title-header">
      <div class="col-12">
        <div class="page-header">
          <b-breadcrumb class="m-0">
            <b-breadcrumb-item
              :to="{
              name: 'DashboardHome',
            }"
            >
              <i class="fa fa-home"></i>
            </b-breadcrumb-item>
            <b-breadcrumb-item active>銷售應用中心</b-breadcrumb-item>
            <b-breadcrumb-item :to="{ name: 'OrderList' }">
              訂單管理
            </b-breadcrumb-item>
            <b-breadcrumb-item active>
              批次上傳
            </b-breadcrumb-item>
          </b-breadcrumb>
        </div>
      </div>
    </div>
    <b-card>
      <b-button-group v-if="canSwitchMode" class="d-flex">
        <b-button
          class="flex-fill"
          :variant="mode === 'simple' ? 'info' : 'secondary'"
          @click="mode = 'simple'"
        >
          精簡
        </b-button>

        <b-button
          class="flex-fill"
          :variant="mode === 'simple' ? 'secondary' : 'info'"
          @click="mode = 'standard'"
        >
          標準
        </b-button>
      </b-button-group>
      <div class="col-12 col-xl-6 d-flex align-items-center">
        <h4 class="mb-2 mb-xl-0 mr-2 font-weight-bold">
          批次上傳-{{ mode === 'simple' ? '精簡' : '標準' }}
        </h4>
        <span id="title" class="mdi mdi-help-circle-outline"></span>
        <b-tooltip target="title" triggers="hover">
          點擊上方，可切換上傳訂單『精簡或標準版』
        </b-tooltip>
      </div>
      <div class="row my-3">
        <div class="col-6 col-xl-6">
          <div class="upload-workflow p-3 p-xl-4">
            <div class="step-block">
              <div class="step-title">Step 1. 請選擇通路</div>
              <b-form-select
                v-model="selectedMerchantID"
                :options="merchants"
                :disabled="isFetchMerchants"
              ></b-form-select>
              <b-form-checkbox class="mt-2" v-model="autoGenerateMember">
                <span class="d-inline-flex align-items-center text-danger">
                  自動建立會員編號
                </span>
                <span id="autoGenerateMemberTip" class="mdi mdi-information-outline ml-1 text-muted"></span>
              </b-form-checkbox>
              <b-tooltip target="autoGenerateMemberTip" triggers="hover">
                只要會員編號或手機號碼其中一項有值，系統即會自動建立對應的 customer 資料。
              </b-tooltip>
            </div>

            <div class="step-block">
              <div class="step-title">Step 2. 下載範例與選擇檔案</div>
              <div class="d-flex flex-wrap align-items-center step2-top-row">
                <b-form-file
                  id="fileInput"
                  :key="fileInputKey"
                  plain
                  hidden
                  @change="handleUploadFile"
                  placeholder="尚未選擇檔案"
                  browse-text="瀏覽"
                ></b-form-file>
                <b-button
                  class="flex-shrink-0"
                  variant="primary"
                  tag="label"
                  for="fileInput"
                >
                  請選擇檔案
                </b-button>

                <div class="selected-file-card ml-2">
                  <span class="selected-file-text text-truncate d-inline-block">
                    {{ selectedFileName ? `已選擇：${selectedFileName}` : "尚未選擇檔案" }}
                  </span>
                </div>
              </div>

              <div class="mt-2">
                <a
                  v-if="mode === 'simple'"
                  href="#"
                  class="sample-file-link"
                  @click.prevent="
                    getSampleExcelFile(
                      getModuleConfig('order', 'sample_file.order_import_light_xls'),
                      '/excel/order_simple_list.xlsx'
                    )
                  "
                >
                  下載範例檔案
                </a>
                <a
                  v-if="mode === 'standard'"
                  href="#"
                  class="sample-file-link"
                  @click.prevent="
                    getSampleExcelFile(
                      getModuleConfig('order', 'sample_file.order_import_xls'),
                      '/excel/order_standard_list.xlsx'
                    )
                  "
                >
                  下載範例檔案
                </a>
              </div>
            </div>

            <div class="step-block mb-0">
              <div class="step-title">Step 3. 確認上傳</div>
              <b-button
                variant="primary"
                @click="handleSubmitFile"
                :disabled="!form.file || !selectedMerchantID || isLoading"
              >
                確定上傳
              </b-button>
              <b-button
                v-if="results.failed.length > 0"
                class="ml-2"
                variant="outline-primary"
                @click="exportFailedOrders"
              >
                下載失敗檔案（{{ results.failed.length }}）
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <div class="accordion" role="tablist">
            <b-card
              no-body
              class="mb-1"
              v-for="(key, index) in Object.keys(results)"
              :key="index"
            >
              <b-card-header header-tag="header" class="p-1" role="tab">
                <b-button
                  v-if="key === 'failed'"
                  block
                  v-b-toggle.accordion-0
                  variant="danger"
                  >失敗：{{ results.failed.length }}筆</b-button
                >
                <b-button
                  v-if="key === 'createOrder'"
                  block
                  v-b-toggle.accordion-1
                  variant="info"
                  >新增訂單資料：{{ results.createOrder.length }}筆</b-button
                >
                <b-button
                  v-if="key === 'createOrderItem'"
                  block
                  v-b-toggle.accordion-2
                  variant="info"
                >新增訂單明細：{{ results.createOrderItem.length }}筆</b-button
                >
                <b-button
                  v-if="key === 'updateOrder'"
                  block
                  v-b-toggle.accordion-3
                  variant="info"
                  >更新訂單資料：{{ results.updateOrder.length }}筆</b-button
                >
                <b-button
                  v-if="key === 'updateOrderItem'"
                  block
                  v-b-toggle.accordion-4
                  variant="info"
                >更新訂單明細：{{ results.updateOrderItem.length }}筆</b-button
                >
              </b-card-header>
              <b-collapse
                :id="`accordion-${index}`"
                visible
                accordion="my-accordion"
                role="tabpanel"
              >
                <b-card-body>
                  <b-table
                    striped
                    hover
                    responsive
                    :items="results[key]"
                    :fields="tableFields(key)"
                    :busy="isLoading"
                  >
                    <template #table-busy>
                      <div class="text-center my-2">
                        <b-spinner class="align-middle"></b-spinner>
                      </div>
                    </template>
                  </b-table>
                </b-card-body>
              </b-collapse>
            </b-card>
          </div>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import customerApi from "../../../apis/customer";
import { getSampleExcelFile } from "@/utils/dashboardUtils";
import "bootstrap-vue/dist/bootstrap-vue.css";
import { mapState, mapGetters } from "vuex";
import * as consts from "@/consts";
import BreadCrumb from "@/components/Dashboard/BreadCrumb";
import excelApi from "@/apis/excel";
import PermissionChecker from "@/utils/PermissionChecker";
import order from "@/apis/order";

export default {
  components: { BreadCrumb },
  data() {
    return {
      consts: consts,
      mode: 'simple',
      isFetchMerchants: false,
      autoGenerateMember: false,
      isRegistered: false,
      isLoading: false,
      selectedMerchantID: null,
      merchants: [{ value: null, text: "請選擇" }],
      form: {
        file: null,
      },
      results: {
        failed: [],
        createOrder: [],
        createOrderItem: [],
        updateOrder: [],
        updateOrderItem: [],
      },
      customers: [],
      uniqueByList: [
        { id: "mobile_phone", name: "手機" },
        { id: "birthday", name: "生日" },
        { id: "email", name: "Email" },
        { id: "outer_code", name: "Outer Code" },
      ],
      fileInputKey: 0,
      selectedFileName: "",
    };
  },

  computed: {
    ...mapGetters({
      getModuleConfigWithMerchant: "dashboardModule/getConfigWithMerchant",
    }),
    ...mapState("general", {
      defaultMerchantType: (state) => state.defaultMerchantType,
    }),
    hasOrderImportLightPermission() {
      return this.checkPermission([consts.ORDER_IMPORT_LIGHT]);
    },
    hasOrderImportPermission() {
      return this.checkPermission([consts.ORDER_IMPORT]);
    },
    canSwitchMode() {
      return this.hasOrderImportLightPermission && this.hasOrderImportPermission;
    },
    // 核心邏輯調整：根據 key 決定顯示哪一部分
    tableFields() {
      return (key) => {
        let fields = [{ key: "row", label: "第幾列" }];
        const isSimple = this.mode === 'simple';

        // 取得該模式下的欄位清單
        const orderData = this.getOrderFields(isSimple);
        const orderDetail = this.getDetailFields(isSimple);

        if (key === 'failed') {
          // 失敗：訂單資料 + 訂單明細 + 失敗原因
          fields = [...fields, ...orderData, ...orderDetail, { key: 'failed_reason', label: '失敗原因' }];
        }
        else if (key === 'createOrder' || key === 'updateOrder') {
          // 訂單資料：顯示綠框內容
          fields = [...fields, ...orderData];
        }
        else if (key === 'createOrderItem' || key === 'updateOrderItem') {
          // 訂單明細：顯示橘框內容
          fields = [...fields, ...orderDetail];
        }

        return fields;
      };
    },
  },
  mounted() {
    if (this.hasOrderImportLightPermission) {
      this.mode = 'simple';
    } else if (this.hasOrderImportPermission) {
      this.mode = 'standard';
    }
  },
  created() {
    if (!this.checkPermissionAny([consts.ORDER_IMPORT_LIGHT, consts.ORDER_IMPORT])) {
      this.$router.replace({
        name: 'NoPermission403',
        params: { org_code: this.$store.state.general.currentOrgCode },
      });
      return;
    }

    this.getMerchants();

    if (this.selectedMerchantID) {
      this.getCustomerList();
    }
  },

  watch: {
    "form.is_binded"() {
      this.getCustomerList();
    },
  },

  methods: {
    getSampleExcelFile,
    // 取得訂單資料欄位 (綠框邏輯)
    getOrderFields(isSimple) {
      const allFields = [
        { key: "outer_order_number", label: "訂單編號" },
        { key: "order_at", label: "訂單日期" },
        { key: "customer_outer_code", label: "會員編號" },
        { key: "name", label: "姓名" },
        { key: "mobile_phone", label: "手機號碼" },
        { key: "total", label: "訂單總價" },
        { key: "status", label: "訂單狀態" },
        { key: "delivery_status", label: "訂單出貨狀態" },
      ];

      if (isSimple) {
        // 精簡版篩選 (移除 訂單編號、訂單總價)
        const simpleKeys = ["order_at", "customer_outer_code", "name", "mobile_phone", "status", "delivery_status"];
        return allFields.filter(f => simpleKeys.includes(f.key));
      }
      return allFields;
    },
    // 取得訂單明細欄位 (橘框邏輯)
    getDetailFields(isSimple) {
      const allFields = [
        { key: "outer_order_item_number", label: "訂單明細編號" },
        { key: "title", label: "商品名稱" },
        { key: "sku_code", label: "商品代號" },
        { key: "item_quantity", label: "明細數量" },
        { key: "item_total_price", label: "明細總價" },
        { key: "item_price", label: "明細單價" },
      ];

      if (isSimple) {
        // 精簡版篩選 (僅保留 明細編號、名稱)
        const simpleKeys = ["outer_order_item_number", "title"];
        return allFields.filter(f => simpleKeys.includes(f.key));
      }
      return allFields;
    },
    checkPermission(permissions) {
      const checker = new PermissionChecker();
      return checker.check(permissions);
    },
    checkPermissionAny(permissions) {
      const checker = new PermissionChecker;
      return checker.checkAny(permissions);
    },
    async getMerchants() {
      this.isFetchMerchants = true;
      const { data } = await customerApi.merchantList();
      this.isFetchMerchants = false;

      this.merchants = [
        { value: null, text: "請選擇" },
        ...data.data.map((merchant) => {
          return {
            value: merchant.id,
            text: merchant.type_name
              ? `${merchant.type_name} (${merchant.name})`
              : merchant.name,
          };
        }),
      ];

      const queryMerchantId = this.$route.query.merchant_hashid;
      if (queryMerchantId && this.merchants.find(m => m.value == queryMerchantId)) {
        this.selectedMerchantID = queryMerchantId;
      } else {
        const defaultMerchant = data.data?.find((merchant) => merchant.type === this.defaultMerchantType) ??
          data.data?.find((merchant) => merchant.type === consts.MERCHANT_TYPES.LINE_TYPE) ;
        this.selectedMerchantID = defaultMerchant?.id;
      }
    },

    handleBack() {
      this.$router.push({ name: "CustomerList" });
    },

    handleUploadFile(e) {
      const files = e.target.files;
      this.form.file = files && files.length ? files[0] : null;
      this.selectedFileName = this.form.file ? this.form.file.name : "";
    },

    async handleSubmitFile() {
      try {
        if (!this.selectedMerchantID) {
          this.$swal.fire({
            icon: "error",
            title: "請選擇通路",
          });
          return;
        }

        this.isLoading = true;

        let handler = 'order_simplified';

        if (this.mode === 'standard') {
          handler = 'order_complex';
        }

        // 將results全部清空
        Object.keys(this.results).forEach(key => {
          this.results[key] = [];
        });

        const response = await excelApi.import(handler, this.form.file, {
          merchant_id: this.selectedMerchantID,
          auto_generate_member: this.autoGenerateMember,
        });

        let data = response.data.stats;

        this.results.failed = data.fail;
        this.results.createOrder = data.order_insert;
        this.results.createOrderItem = data.order_item_insert;
        this.results.updateOrder = data.order_update;
        this.results.updateOrderItem = data.order_item_update;

        this.isLoading = false;
      } catch (e) {
        console.log(e);
      } finally {
        this.form.file = null;     // ⭐ 清掉舊 File reference
        this.selectedFileName = "";
        this.fileInputKey++;       // ⭐ 強制重建 file input
        this.isLoading = false;
      }
    },
    async exportFailedOrders() {
      try {
        let failed = this.results.failed;
        let params = {orders: failed, mode: this.mode};

        await order.exportFailedOrders(this.selectedMerchantID, params).then(() => {
          this.$swal({
            title: '成功',
            text: "可至“我的主控台”查看紀錄及下載檔案",
            showCancelButton: true,
            cancelButtonText: '確認',
            type: 'success',
            confirmButtonText: '前往查看',
          }).then((result)=>{
            if (result.value) {
              this.$router.push({
                name: "MyConsoleDownloadFileList",
                params: { org_code: this.$route.params.org_code },
              });
            }
          });
        });
      } catch (e) {
        console.log(e)
      }
    },
    getModuleConfig(module, key) {
      return this.getModuleConfigWithMerchant(module, key, this.selectedMerchantID);
    },
  },
};
</script>

<style scoped>
.upload-workflow {
  border: 0;
  border-radius: 0;
  background: transparent;
}

.step-block {
  margin-bottom: 1rem;
}

.step-title {
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
}

.selected-file-card {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0.3rem 0.55rem;
  border: 1px solid #d7dce2;
  border-radius: 4px;
  background-color: #f9fafb;
  color: #4b5563;
}

.selected-file-text {
  max-width: 100%;
  font-size: 0.85rem;
  line-height: 1.25;
}

.step2-top-row {
  column-gap: 0.5rem;
  row-gap: 0.5rem;
}

.sample-file-link {
  color: #1d4ed8;
  font-size: 0.9rem;
  text-decoration: underline;
}

.sample-file-link:hover {
  color: #1e40af;
}

@media (max-width: 1200px) {
  .upload-workflow {
    margin-top: 0.5rem;
  }

  .step-block {
    margin-bottom: 0.85rem;
  }

  .selected-file-card {
    margin-left: 0 !important;
  }
}
</style>
